import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Divider,
  Alert
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  PlaylistPlay as RunAllIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as ReadyIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import qualityService from '../../services/qualityService';
import { getAvatarImage } from '../../utils/avatarImages';

const QualityAnalysis = ({ projectId, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [sections, setSections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningAgents, setRunningAgents] = useState(new Set());
  const [agentResults, setAgentResults] = useState({});

  useEffect(() => {
    loadQualityData();
  }, []);

  const loadQualityData = async () => {
    try {
      setLoading(true);
      const sectionsResponse = await qualityService.getSections();
      const agentsResponse = await qualityService.getAgents();
      
      if (sectionsResponse.success) {
        setSections(sectionsResponse.data);
        // Select first section by default
        if (sectionsResponse.data.length > 0) {
          setSelectedSection(sectionsResponse.data[0]);
        }
      }
      
      if (agentsResponse.success) {
        setAgents(agentsResponse.data);
      }
    } catch (err) {
      console.error('Error loading quality data:', err);
      setError('Failed to load quality analysis data');
    } finally {
      setLoading(false);
    }
  };

  const executeAgent = async (agent) => {
    if (!projectId) {
      console.error('No project ID provided');
      return;
    }

    try {
      setRunningAgents(prev => new Set([...prev, agent.id]));
      
      const response = await qualityService.executeAgent(agent.id, projectId);
      
      if (response.success) {
        setAgentResults(prev => ({
          ...prev,
          [agent.id]: response.data
        }));
      }
    } catch (err) {
      console.error(`Error executing agent ${agent.id}:`, err);
    } finally {
      setRunningAgents(prev => {
        const newSet = new Set(prev);
        newSet.delete(agent.id);
        return newSet;
      });
    }
  };

  const executeAllAgentsInSection = async (section) => {
    if (!projectId) {
      console.error('No project ID provided');
      return;
    }

    try {
      const sectionAgents = agents.filter(agent => agent.section_id === section.id);
      
      // Set all section agents as running
      setRunningAgents(prev => new Set([...prev, ...sectionAgents.map(a => a.id)]));
      
      const response = await qualityService.executeSectionAgents(section.id, projectId);
      
      if (response.success) {
        // Update results for all agents in section
        const newResults = {};
        response.data.forEach((result, index) => {
          if (sectionAgents[index]) {
            newResults[sectionAgents[index].id] = result;
          }
        });
        
        setAgentResults(prev => ({
          ...prev,
          ...newResults
        }));
      }
    } catch (err) {
      console.error(`Error executing section ${section.id} agents:`, err);
    } finally {
      // Remove all section agents from running state
      const sectionAgents = agents.filter(agent => agent.section_id === section.id);
      setRunningAgents(prev => {
        const newSet = new Set(prev);
        sectionAgents.forEach(agent => newSet.delete(agent.id));
        return newSet;
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return theme.palette.success.main;
      case 'running':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[400];
    }
  };

  const getStatusIcon = (agent) => {
    const isRunning = runningAgents.has(agent.id);
    const hasResult = agentResults[agent.id];
    
    if (isRunning) {
      return <CircularProgress size={12} color="warning" />;
    } else if (hasResult) {
      return <CheckIcon sx={{ fontSize: 12, color: theme.palette.success.main }} />;
    } else {
      return <ReadyIcon sx={{ fontSize: 12, color: theme.palette.grey[400] }} />;
    }
  };

  const filteredAgents = selectedSection 
    ? agents.filter(agent => agent.section_id === selectedSection.id)
    : [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={loadQualityData} startIcon={<RefreshIcon />} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex',}}>
      {/* Left Sidebar - Sections */}
      <Paper 
        sx={{ 
          width: isMobile ? '100%' : 300, 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 0,
          borderRight: `1px solid ${theme.palette.divider}`
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" fontWeight="bold">
            Quality analysis
          </Typography>
        </Box>

        {/* Sections List */}
        <List sx={{ flex: 1, py: 0 }}>
          {sections.map((section) => (
            <Box key={section.id}>
              <ListItem
                button
                selected={selectedSection?.id === section.id}
                onClick={() => setSelectedSection(section)}
                sx={{
                  py: 2,
                  backgroundColor: selectedSection?.id === section.id 
                    ? `${section.color}15` 
                    : 'transparent',
                  borderLeft: selectedSection?.id === section.id 
                    ? `4px solid ${section.color}` 
                    : 'none',
                  '&:hover': {
                    backgroundColor: `${section.color}10`
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar 
                    src={getAvatarImage(section.orchestrator.avatarImage)}
                    sx={{ 
                      backgroundColor: section.orchestrator.avatarImage ? 'transparent' : `${section.color}20`,
                      color: section.color,
                      width: 32,
                      height: 32,
                      fontSize: '0.875rem'
                    }}
                  >
                    {!section.orchestrator.avatarImage && section.orchestrator.avatar}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" fontWeight="medium">
                      {section.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Orchestrator: {section.orchestrator.name}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {section.agentCount} agents • {section.taskCount} tasks
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      executeAllAgentsInSection(section);
                    }}
                    disabled={runningAgents.size > 0}
                    sx={{ color: section.color }}
                  >
                    <RunAllIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </Box>
          ))}
        </List>
      </Paper>

      {/* Right Content - Agent Tiles */}
      {!isMobile && (
        <Box sx={{ flex: 1, py: 3, px: 4, overflow: 'auto' }}>
          {selectedSection && (
            <>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {selectedSection.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedSection.description}
              </Typography>

              <Grid container spacing={2}>
                {filteredAgents.map((agent) => (
                  <Grid item xs={12} sm={6} lg={4} key={agent.id}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        height: '100%',
                        backgroundColor: `${agent.color}05`,
                        border: `1px solid ${agent.color}30`,
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[4]
                        }
                      }}
                    >
                      {/* Status indicator in top-right */}
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 12, 
                          right: 12, 
                          zIndex: 1 
                        }}
                      >
                        {getStatusIcon(agent)}
                      </Box>

                      <CardContent sx={{ pb: 1 }}>
                        {/* Agent avatar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar 
                            src={getAvatarImage(agent.avatarImage)}
                            sx={{ 
                              width: 40, 
                              height: 40,
                              backgroundColor: agent.avatarImage ? 'transparent' : agent.color,
                              color: 'white',
                              fontSize: '1.25rem',
                              fontWeight: 'bold',
                              mr: 2 
                            }} 
                          >
                            {!agent.avatarImage && agent.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" fontWeight="medium">
                              {agent.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {agent.estimated_time}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {agent.description}
                        </Typography>

                        {/* Tasks list */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" fontWeight="medium" color="text.secondary">
                            TASKS:
                          </Typography>
                          {agent.tasks?.slice(0, 2).map((task, index) => (
                            <Typography key={index} variant="caption" display="block" color="text.secondary">
                              • {task}
                            </Typography>
                          ))}
                          {agent.tasks?.length > 2 && (
                            <Typography variant="caption" color="text.secondary">
                              +{agent.tasks.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      </CardContent>

                      {/* Play button in bottom-right */}
                      <IconButton
                        onClick={() => executeAgent(agent)}
                        disabled={runningAgents.has(agent.id)}
                        sx={{
                          position: 'absolute',
                          bottom: 12,
                          right: 12,
                          backgroundColor: agent.color,
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            backgroundColor: agent.color,
                            opacity: 0.8
                          },
                          '&:disabled': {
                            backgroundColor: theme.palette.grey[300]
                          }
                        }}
                      >
                        {runningAgents.has(agent.id) ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PlayIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default QualityAnalysis;